/**
 * The end card and the queue page's words and links (AD-794; design `docs/design/guest-demo.md`
 * §6.4 "never bait-and-switch", §6.5 launch mode; story
 * `q-guest-demo-s5-client-guest-mode-and-end-card`). Pure, so both launch modes and every
 * reason are unit-tested copy, not something a reviewer has to film to read.
 *
 * LAUNCH MODE (`GET /api/guest/config` `registrationOpen`, one env line on the guest process):
 *   - closed (launch): the primary call to action is the monthly development newsletter ("Get the monthly newsletter") and
 *     the socials follow ("Follow the road"); there is no sign-up button, because there is no
 *     public sign-up to send anyone to yet;
 *   - open: "Create your account" leads; the newsletter, devlog and socials follow.
 *
 * Every link is `/api/guest/out/<target>` (the server counts the click and redirects — the client
 * never sends an analytics message) and opens in a NEW TAB, so a running demo or a queue place is
 * never lost to a click. Only targets the server lists in `ctaTargets` are shown.
 *
 * FOUNDER INTEREST (AD-810 item 5, story `q-guest-demo-founder-interest-signup`): a third prompt
 * beside the newsletter and the socials, into a SEPARATE founder-interest Brevo list. It follows
 * the newsletter in both modes and appears only when the server serves the target (its form is
 * configured). Its words promise news, never access: anyone signing up after the list cut lands
 * in the final closed-testing wave (AD-810 item 3), so nothing here says "early", "earliest" or
 * "first in", and the end card no longer says "be first through them".
 *
 * COMMUNITY (q-guest-end-card-conversion-pass, 2026-10-24, from the Argon + Astra reviews): Discord is
 * the community the owner ranks first, so it leaves the "Follow the road" row and becomes its own
 * named action ("Join the Discord") after the founder prompt; the other socials stay in the row.
 *
 * WEBSITE (owner, 2026-11-02): "Visit the website" sits beside "Watch how it's made" in both modes,
 * so the company site (About, the other games, press) is one obvious click from the card; the
 * devlog link reaches the site too, but mid-page and under a name that does not say so.
 *
 * Copy rules: "The Great Cloud" is never shortened (the brand guard), and what The Great Cloud is
 * or does is never explained — the Twin Minds hold the road real; nobody here says it is. No
 * scarcity, price or rate number appears (AD-321).
 */

import { GAME_NAME, TRADE_MARK } from '@great-cloud/shared/client';

import { guestOutHref } from './guestApi.js';
import type { GuestCtaSource, GuestCtaTarget } from './guestApi.js';
import type { GuestEndReason } from './guestFlow.js';

export interface GuestCta {
  target: GuestCtaTarget;
  label: string;
  /** Always `/api/guest/out/<target>` — never a destination URL. */
  href: string;
}

export interface GuestCtaBlock {
  primary: GuestCta | null;
  secondary: GuestCta[];
  /** The "Follow the road" row. */
  socials: GuestCta[];
}

const LABELS: Readonly<Record<GuestCtaTarget, string>> = {
  newsletter: 'Get the monthly newsletter',
  register: 'Create your account',
  devlog: "Watch how it's made",
  website: 'Visit the website',
  social_discord: 'Discord',
  social_x: 'X',
  social_telegram: 'Telegram',
  social_tiktok: 'TikTok',
  social_instagram: 'Instagram',
  social_youtube: 'YouTube',
  founder_interest: 'Hear when founder packs open',
};

/** Discord as a named action of its own, not one icon among five (see COMMUNITY above). */
const COMMUNITY_LABEL = 'Join the Discord';

const SOCIAL_ORDER: readonly GuestCtaTarget[] = [
  'social_discord',
  'social_x',
  'social_telegram',
  'social_tiktok',
  'social_instagram',
  'social_youtube',
];

/**
 * The CTA block for a launch mode. `available` = the server's `ctaTargets` (closed set). `from`
 * marks a click from somewhere other than the end card / queue page (AD-833: the death card) —
 * the SAME targets in the same order, only counted apart.
 */
export function guestCtaBlock(
  registrationOpen: boolean,
  available: readonly GuestCtaTarget[],
  base?: string,
  from?: GuestCtaSource,
): GuestCtaBlock {
  const has = new Set(available);
  const cta = (target: GuestCtaTarget): GuestCta | null =>
    has.has(target)
      ? { target, label: LABELS[target], href: guestOutHref(target, base, from) }
      : null;
  const present = (list: (GuestCta | null)[]): GuestCta[] =>
    list.filter((c): c is GuestCta => c !== null);
  const discord = cta('social_discord');
  const community = discord ? { ...discord, label: COMMUNITY_LABEL } : null;
  const socials = present(SOCIAL_ORDER.filter((t) => t !== 'social_discord').map(cta));
  if (registrationOpen) {
    return {
      primary: cta('register'),
      secondary: present([
        cta('newsletter'),
        cta('founder_interest'),
        community,
        cta('devlog'),
        cta('website'),
      ]),
      socials,
    };
  }
  // Launch mode: no sign-up link at all — registration is not public yet (§6.5).
  return {
    primary: cta('newsletter'),
    secondary: present([cta('founder_interest'), community, cta('devlog'), cta('website')]),
    socials,
  };
}

/**
 * The newsletter as a standing Menu item during a demo (a visitor said it was hard to find,
 * 2026-10-30). The SAME `/api/guest/out/newsletter` link and words as the end card, counted like
 * the banner's (no `from`), and only when the server serves the target. It is a link in the Menu
 * the player opens, never a prompt: nothing interrupts play to offer it.
 */
export function guestMenuNewsletter(
  available: readonly GuestCtaTarget[],
  base?: string,
): GuestCta | null {
  return available.includes('newsletter')
    ? { target: 'newsletter', label: LABELS.newsletter, href: guestOutHref('newsletter', base) }
    : null;
}

export interface EndCardCopy {
  heading: string;
  lines: string[];
  /** The one-line footnote under the buttons. */
  note: string;
}

/** The founder prompt's promise, shown under the offers whenever that prompt is (AD-810). */
export const FOUNDER_FINE_PRINT = 'Founder packs are cosmetic only — never power.';

/**
 * The end card's words (§6.4 / §6.5), by launch mode and by why the demo ended. A player who
 * finished the road (`completed`: Sel's final turn-in, after the Sentinel) is told so first; the
 * Twin Minds' framing (Q-G2) follows in the same heading, so every ending still says it was one road.
 */
export function endCardCopy(registrationOpen: boolean, reason: GuestEndReason): EndCardCopy {
  const heading =
    reason === 'expired'
      ? "Time's up — that was one road."
      : reason === 'completed'
        ? 'You beat the Sentinel — and that was one road.'
        : 'That was one road.';
  const glimpse =
    `You just played a glimpse of ${GAME_NAME}${TRADE_MARK} as a level-10 adventurer — a possibility the ` +
    'Twin Minds hold real. Your demo character, items and progress are not saved.';
  const realStart = registrationOpen
    ? 'Your real adventure starts at level 1, in the Measured Yard, with a practice blade and a ' +
      'tunic — and every road open.'
    : 'Your real adventure starts at level 1 when accounts open.';
  const note =
    reason === 'lost'
      ? "The connection dropped, and demo sessions aren't saved — Play again starts a fresh one."
      : "Demo sessions aren't saved. Links open in a new tab.";
  return { heading, lines: [glimpse, realStart], note };
}

/**
 * The death card's words (AD-833; design §5.6.3). A guest who falls chooses Try again (the held
 * respawn at the waystone) or the end card's own offers; an offer never ends the demo. No number,
 * no promise of what the fight will do next (the Twin Minds hold the road; nobody explains it).
 */
export function deathCardCopy(retrying: boolean): {
  heading: string;
  line: string;
  tryAgain: string;
  note: string;
} {
  return {
    heading: "You fell — this road isn't finished.",
    line: 'The Twin Minds hold every road real, and this one is still open. Try again to rise at the waystone.',
    tryAgain: retrying ? 'Rising at the waystone…' : 'Try again',
    note: 'Links open in a new tab — your demo keeps its place.',
  };
}

/** The queue page's words (§7.1): the real position, an honest "about" ETA or none at all. */
export function queueCopy(
  position: number,
  etaSeconds: number | null,
  registrationOpen: boolean,
): { heading: string; position: string; eta: string | null; hint: string } {
  const minutes = etaSeconds === null ? null : Math.max(1, Math.round(etaSeconds / 60));
  return {
    heading: 'The demo is full right now — you are in line.',
    position: position <= 1 ? 'You are next.' : `Your place in line: ${position}`,
    eta: minutes === null ? null : `About ${minutes} minute${minutes === 1 ? '' : 's'} to go.`,
    hint: registrationOpen
      ? 'Keep this tab open to hold your place — create your account in a new tab while you wait.'
      : 'Keep this tab open to hold your place — the links below open in a new tab.',
  };
}
